<template>
  <div>
    <TransactionalPageBreadcrumbs
      class="my-4"
      :city-name="citySlugLocalityName"
      :city-slug="citySlug"
      :category-slug="categSlug"
      :tag-slug="tagSlug"
      :general-slug="generalSlug"
      :type="pageType as EventListPageType"
      :event-list-page-obj="eventListPageData"
    />

    <div class="event-list-page__section mb-0">
      <div
        class="relative h-[80px] md:h-auto rounded-xl overflow-hidden md:overflow-visible flex items-center"
      >
        <NuxtImg
          :preload="true"
          class="card-image object-cover w-full md:hidden"
          src="/_nuxt3-static/general/transactional-pages-banner.webp"
          alt="Ein Barkeeper gießt Cocktails in zwei Gläser."
          loading="eager"
        />

        <div
          class="w-full h-full bg-blend-hard-light bg-gradient-to-r from-white to-transparent absolute"
        ></div>

        <Heading
          v-if="searchHeading"
          class="absolute md:relative flex md:justify-start justify-center items-center w-full h-full pt-2"
          h-class="!text-teal-900 !text-xl font-semibold"
          :title="searchHeading"
          :level="1"
        >
        </Heading>
      </div>
    </div>
    <FilteredProductShowcase
      :page-type="pageType as EventListPageType"
      :event-list-page-data="eventListPageData"
      :load-schema="true"
      :reviews-data="reviewsData"
    />

    <ReviewsTransactional
      :reviews="rawReviews"
      :title="eventListPageData?.title"
    />

    <NuxtLazyHydrate when-visible>
      <ProductBoxShowcase
        v-if="['category', 'city-category'].includes(pageType)"
        :title="
          t('eventListPage.kitsTitle', {
            category: capitalizedCategorySlug,
          })
        "
        title-class="text-h3"
        :category="categSlug"
        :is-duration-visible="false"
        is-mobile-slider
      />
    </NuxtLazyHydrate>
    <NuxtLazyHydrate when-visible>
      <TransactionalPageCityCategoriesList
        v-if="EventListPageType.CITY === pageType"
        :subtitle="eventListPageData?.title"
        :city-slug="citySlug"
        :category-slug="categSlug"
        class="pt-10"
        :section-title="
          t('eventListPage.city.cityCategoryHorizontalListTitle', {
            city: citySlugLocalityName,
          })
        "
        data-test-id="city-category-horizontal-list"
      />
    </NuxtLazyHydrate>
    <NuxtLazyHydrate when-visible>
      <TransactionalPageCityCategoriesList
        v-if="EventListPageType.CATEGORY === pageType"
        :city-slug="undefined"
        :category-slug="categSlug"
        class="pt-10"
        :section-title="
          t('eventListPage.category.categoryCitiesHorizontalListTitle', {
            category: capitalizedCategorySlug,
          })
        "
        data-test-id="category-cities-horizontal-list"
      />
    </NuxtLazyHydrate>
    <NuxtLazyHydrate when-visible>
      <TransactionalPageCardListFromStrapi
        v-if="shouldShowHorizontalCardListFromStrapi"
        :event-list-page="eventListPageData"
        class="pt-10"
        data-test-id="horizontal-card-list"
      />
    </NuxtLazyHydrate>
    <NuxtLazyHydrate when-visible>
      <vue-markdown
        class="prose my-2"
        :source="eventListPageAttributes?.description || ''"
      ></vue-markdown>
    </NuxtLazyHydrate>

    <div class="mt-10">
      <Heading
        :level="4"
        :title="strapiEventListPageGetters.getShowcaseTitle(eventListPageData)"
        h-class="text-h3 text-stone-800"
        class="kft-heading--left mb-10"
      />
      <NuxtLazyHydrate when-visible>
        <SimpleCardShowcase
          :showcase-items="cardShowcase"
          data-test-id="simple-showcase"
        />
      </NuxtLazyHydrate>
    </div>
  </div>
</template>

<script setup lang="ts">
import VueMarkdown from 'vue-markdown-render'
import {
  EventListPageType,
  type EventListPageTypeString,
  type StrapiEventListPage,
} from '~/composables/types/strapiEventListPage'
import type { KftApiDefaultResponse } from '~/composables/types/kft-api'
import { useTransactionalPageResource } from '~/composables/transactionalPage/useTransactionalPageResource'
import { useTransactionalPageReviews } from '~/composables/transactionalPage/useTransactionalPageReviews'
import { eventListPageLocalitiesUrl } from '~/components/transactionalPage/requestParams'
import { dynamicContentTransformer } from '~/utils/dynamicContentTransformer'

const route = useRoute()
let citySlug: string | undefined = route?.params?.city?.toString()
const categSlug = route.params?.category?.toString() || ''
let generalSlug = route.params?.slug?.toString()
let tagSlug = generalSlug

const props = withDefaults(
  defineProps<{
    pageType: EventListPageTypeString
  }>(),
  {}
)

const { t } = useI18n()
const localisedTeamEventSlugPrefix = t('eventListPage.teamEventSlugPrefix')

if (props.pageType === EventListPageType.TEAMEVENT) {
  generalSlug = `${localisedTeamEventSlugPrefix}-${citySlug}`
  tagSlug = `${localisedTeamEventSlugPrefix}-${citySlug}`
  citySlug = undefined
}

const { locale } = useI18n()

const { data: dynamicContent } = await useFetchEventListPageDynamicContent(
  props.pageType,
  { categorySlug: categSlug, citySlug, generalSlug }
)

const strapiEventListPageWithNoLocaleFilter = await useStrapiEventListPage({
  slug: generalSlug,
  citySlug,
  categorySlug: categSlug,
})

const transactionalPageResource = await useTransactionalPageResource({
  strapiEventListPageWithNoLocaleFilter,
})

const eventListPageData = computed<StrapiEventListPage>(() => {
  // This takes in consideration that we have less than 10 languages
  // Which means that the paginated results will always include
  // The current translation
  //
  // Past 10 languages, this code would not find some of them
  const entry =
    transactionalPageResource?.data?.value?.find(
      (entry) => entry?.locale.toLowerCase() === locale.value
    ) || transactionalPageResource?.data?.value?.[0]

  const returnObj =
    strapiEventListPageGetters.getLocalizationByLocale(entry, locale.value) ||
    entry

  if (dynamicContent.value?.data) {
    return dynamicContentTransformer(returnObj, dynamicContent.value.data)
  }

  return returnObj
})

if (!eventListPageData.value) {
  throw showError({ statusCode: 404, statusMessage: 'Page Not Found' })
}

const eventListPageAttributes = computed(() => {
  return eventListPageData?.value
})

const shouldShowHorizontalCardListFromStrapi = computed(() => {
  return (
    strapiEventListPageGetters.getHorizontalCardList(eventListPageData.value)
      .length > 0 && props.pageType !== EventListPageType.CATEGORY
  )
})

const cardShowcase = computed(() => {
  return Object.keys(eventListPageAttributes.value?.showcase || {})
    ?.map((key) => eventListPageAttributes.value?.showcase[key])
    .filter((cardDetails) => cardDetails?.id)
    .map((cd) => ({
      ...cd,
      imageUrl: strapiImageGetters.getImageUrl(cd.image),
    }))
})

const searchHeading = computed(
  () =>
    strapiEventListPageGetters.getSearchHeading(eventListPageData.value) ||
    strapiEventListPageGetters.getTitle(eventListPageData.value)
)

const shouldFetchLocalities = [
  EventListPageType.CITY_CATEGORY,
  EventListPageType.CITY,
].includes(props.pageType as EventListPageType)

// Needed in:
// 1. city page for the horizontal city-category list
// 2. city-category page for the breadcrumbs
const { data: localityResponse } =
  (shouldFetchLocalities &&
    (await useKftApiFetch<KftApiDefaultResponse<{ name: string }>>(
      eventListPageLocalitiesUrl({ citySlug })
    ))) ||
  {}

const citySlugLocalityName = localityResponse?.value?.data.name

const capitalizedCategorySlug = computed(() => {
  if (!categSlug) return ''

  if (categSlug.toLowerCase() === 'diy') return 'DIY'

  return categSlug.charAt(0).toUpperCase() + categSlug.slice(1)
})

// Load reviews data for schema before rendering components
const getReviewsSlug = computed(() => {
  const citySlugValue = eventListPageData.value?.citySlug
  const categorySlugValue = eventListPageData.value?.categorySlug

  if (citySlugValue && categorySlugValue) {
    return citySlugValue + '-' + categorySlugValue
  }

  if (citySlugValue) {
    return citySlugValue
  }

  return categorySlugValue || ''
})

const { reviews: rawReviews } = await useTransactionalPageReviews(
  props.pageType,
  getReviewsSlug.value,
  locale.value
)

// Transform reviews data for schema
const reviewsData = computed(() => {
  if (!rawReviews.value || rawReviews.value.length === 0) return null

  const transformedReviews = rawReviews.value.map((review: any) => ({
    ...review,
    created_at: review.review_date,
    id: review.review_id,
    rating: parseFloat(review.event_avg_review),
    customer: review.reviewee_name,
    review: review.review_text,
    reviewee_name: review.reviewee_name,
    review_text: review.review_text,
    review_date: review.review_date,
  }))

  const sum = rawReviews.value.reduce(
    (acc: number, review: any) =>
      acc + parseFloat(review?.event_avg_review || '5'),
    0
  )
  const average = sum / rawReviews.value.length
  const averageRating = new Intl.NumberFormat(locale.value, {
    maximumSignificantDigits: 2,
    minimumSignificantDigits: 2,
  }).format(average)

  return {
    reviews: transformedReviews,
    averageRating,
    reviewCount: rawReviews.value.length,
  }
})
</script>

<style lang="postcss">
.prose {
  max-width: 100%;
  width: 100%;
  table {
    width: unset;
  }
}
</style>
